/*
  DAKI5Sdf (Step 2) — pre-checkout product-photo upload on the model showroom.

  Wires the buy-box add-to-cart flow to the EXISTING studio-backend signed-URL
  service (POST /signed-url → PUT to GCS → the file persists under
  customers/<uid>/uploads/). On a successful upload it stamps the resulting
  reference onto the add-to-cart form as line-item properties so the product
  image rides cart → checkout → order email → admin order detail — closing the
  "order doesn't capture the product image" break (bug 6a596df9 / SC6AZdRM #1)
  at the storefront level, independent of the orders/paid webhook secret.

    properties[Source image]     = signed GET URL (human-clickable in the order
                                   email + Shopify admin; expires, but is enough
                                   for the operator to grab the photo promptly)
    properties[Source image ref] = customers/<uid>/uploads/<file>
                                   (durable GCS object key; bucket-independent.
                                   The fulfillment identity bridge already
                                   resolves email→uid→these paths — this just
                                   makes the exact object explicit on the order)

  Auth: reuses the global firebase-auth.js app via getApp() (studio-prod, the
  customer project). Upload is Firebase-token gated — the backend requires a
  verified email. Guests / signed-out visitors are NOT blocked from buying:
  the zone shows a sign-in prompt and the order falls back to the existing
  "reply to your confirmation email with your product photo" path. Upload is
  OPTIONAL by design so a signed-out visitor can still complete a purchase.

  Project-agnostic and defensive: every hook is guarded, so the module is a
  no-op on any page without the showroom buy box or before firebase inits.
*/
import { getApp, getApps } from 'https://www.gstatic.com/firebasejs/10.14.1/firebase-app.js';
import { getAuth, onAuthStateChanged } from 'https://www.gstatic.com/firebasejs/10.14.1/firebase-auth.js';
import { authedFetch, renderStatus, vt } from '@studio/verify-email';

// Card wtBL9rek: the showroom section emits window.Shopify.studioShowroomStrings from the
// showroom.* locale keys (this asset cannot run `| t`), so the messages the buyer sees WHILE
// uploading are in the page's language, not just the static markup. A missing or empty key
// falls back to the English literal, never to a blank. Same shape as studio-signin.js's st().
function st(key, fallback) {
  const v = globalThis.Shopify?.studioShowroomStrings?.[key];
  return typeof v === 'string' && v.trim() ? v : fallback;
}

const zone = document.querySelector('[data-precheckout-upload]');
const form = document.querySelector('form[data-type="add-to-cart-form"]');

if (zone && form && getApps().length) {
  const BACKEND   = (zone.getAttribute('data-backend-url') || '').replace(/\/$/, '');
  const drop      = zone.querySelector('[data-preupload-drop]');
  const input     = zone.querySelector('[data-preupload-input]');
  const statusEl  = zone.querySelector('[data-preupload-status]');
  const previewEl = zone.querySelector('[data-preupload-preview]');
  const promptEl  = zone.querySelector('[data-preupload-signin]');
  const addLabel  = zone.querySelector('[data-preupload-label]');

  const urlInput = form.querySelector('input[name="properties[Source image]"]');
  const refInput = form.querySelector('input[name="properties[Source image ref]"]');

  const auth = getAuth(getApp());
  const ALLOWED_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp']);
  const MAX_BYTES = 15 * 1024 * 1024;
  let busy = false;

  // uGqPRiwp: `status` is the HTTP status behind `msg`. 403 = the backend's
  // email_verified gate — the shared writer mounts the resend control so the
  // message is actionable.
  const setStatus = (msg, isErr, status) => renderStatus(statusEl, msg, isErr, status);

  async function api(path, opts) {
    // Token, Authorization header and the retry-once on the email_verified
    // gate: authedFetch (studio-verify-email.js).
    const res = await authedFetch(BACKEND + path, opts);
    if (!res.ok) {
      let msg = st('failed', 'Upload failed ({status})').replace('{status}', res.status);
      if (res.status === 403) msg = vt('gate_uploads');
      if (res.status === 415) msg = st('bad_type', 'That file type is not supported. Use JPG, PNG or WebP.');
      if (res.status === 429) msg = st('limit', 'You have reached your upload limit.');
      const e = new Error(msg); e.status = res.status; throw e;
    }
    return res.status === 204 ? null : res.json();
  }

  // Reflect a successful upload onto the order (line-item properties) + preview.
  function stamp(gcsRef, displayUrl, fileName) {
    if (refInput) refInput.value = gcsRef || '';
    if (urlInput) urlInput.value = displayUrl || '';
    if (previewEl) {
      previewEl.textContent = '';
      const wrap = document.createElement('div');
      wrap.className = 'ai-model-preupload-thumb';
      if (displayUrl) {
        const img = document.createElement('img');
        img.loading = 'lazy';
        img.src = displayUrl;
        img.alt = fileName || st('heading', 'Your product photo');
        wrap.appendChild(img);
      }
      const cap = document.createElement('span');
      cap.className = 'ai-model-preupload-name';
      cap.textContent = fileName || st('attached_name', 'Product photo attached');
      wrap.appendChild(cap);
      previewEl.appendChild(wrap);
      previewEl.hidden = false;
    }
    setStatus(st('attached', '✓ Product photo attached to your order.'));
  }

  async function uploadOne(file) {
    // 1) scoped, size-bounded V4 signed PUT URL
    const signed = await api('/signed-url', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename: file.name, contentType: file.type, size: file.size }),
    });
    // 2) PUT straight to GCS, echoing the exact signed headers (byte-for-byte
    //    contract: x-goog-content-length-range is baked into the V4 signature).
    const headers = Object.assign(
      { 'Content-Type': signed.contentType || file.type },
      signed.requiredHeaders || {}
    );
    const put = await fetch(signed.url, { method: 'PUT', headers, body: file });
    if (!put.ok) throw new Error(st('failed', 'Upload failed ({status})').replace('{status}', put.status));

    // 3) resolve a human-clickable display URL for the order (signed GET).
    //    The durable ref is the bucket-independent GCS object key (gcsPath);
    //    /uploads returns the freshest signed GET URL for the object we wrote.
    let displayUrl = '';
    const gcsRef = signed.gcsPath || '';
    try {
      const listing = await api('/uploads', { method: 'GET' });
      const match = (listing && listing.uploads || []).find((u) => u.gcsPath === signed.gcsPath);
      if (match) displayUrl = match.url;
    } catch (_e) {
      // GUARD-OK: the upload ALREADY SUCCEEDED — this listing call only fetches a
      // thumbnail URL for the preview. Losing it costs a picture, never the
      // order: `gcsPath` is what links the file to the line item.
      console.debug('[studio] upload preview URL unavailable', _e);
    }
    return { gcsRef, displayUrl, fileName: file.name };
  }

  async function handleFiles(files) {
    const file = [...files].find((f) => ALLOWED_TYPES.has(f.type));
    if (!file || busy) {
      if (files.length && !file) setStatus(st('use_image', 'Use a JPG, PNG or WebP image.'), true);
      return;
    }
    if (file.size > MAX_BYTES) {
      setStatus(st('too_big', '{name} is over the 15 MB limit.').replace('{name}', file.name), true);
      return;
    }
    busy = true;
    setStatus(st('uploading', 'Uploading…'));
    if (drop) drop.classList.add('is-disabled');
    if (input) input.disabled = true;
    if (addLabel) addLabel.textContent = st('uploading', 'Uploading…');
    try {
      const { gcsRef, displayUrl, fileName } = await uploadOne(file);
      stamp(gcsRef, displayUrl, fileName);
    } catch (e) {
      if (e.message === 'not-signed-in') {
        setStatus(st('signin_status', 'Sign in to attach your product photo now, or reply to your order email with it later.'), true);
      } else {
        setStatus(e.message, true, e.status);
      }
    } finally {
      busy = false;
      if (drop) drop.classList.remove('is-disabled');
      if (input) input.disabled = false;
      if (addLabel) addLabel.textContent = st('add', 'Add product photo');
    }
  }

  if (input) {
    input.addEventListener('change', (e) => { handleFiles(e.target.files); input.value = ''; });
  }
  if (drop) {
    drop.addEventListener('dragover',  (e) => { e.preventDefault(); drop.classList.add('is-over'); });
    drop.addEventListener('dragleave', () => drop.classList.remove('is-over'));
    drop.addEventListener('drop', (e) => {
      e.preventDefault();
      drop.classList.remove('is-over');
      if (auth.currentUser) handleFiles(e.dataTransfer.files);
    });
  }

  // lmXZY772 row 2: the Liquid href carries `request.path` only, so a guest who
  // picked a model (`?model=…`) signed in and came back WITHOUT it. Recompose the
  // return URL from the live location — pathname + search — through the ONE
  // helper (window.__studioAuth.withReturn, EAPAHvZu), on reveal and again on
  // click (the composer can change the query after load).
  const signinLink = promptEl && promptEl.querySelector('a[href]');
  function refreshSigninHref() {
    if (!signinLink) return;
    const base = new URL(signinLink.getAttribute('href'), window.location.origin).pathname;
    const ret = window.location.pathname + window.location.search;
    signinLink.setAttribute('href', (window.__studioAuth && window.__studioAuth.withReturn)
      ? window.__studioAuth.withReturn(ret, base)
      : base + '?return_url=' + encodeURIComponent(ret));
  }
  if (signinLink) signinLink.addEventListener('click', refreshSigninHref);

  // Signed-in vs guest: reveal the drop zone to authed users; show the sign-in
  // prompt to guests. Upload is optional either way — never blocks Add to Cart.
  onAuthStateChanged(auth, (user) => {
    const signedIn = !!user;
    if (drop) drop.hidden = !signedIn;
    if (input) input.disabled = !signedIn;
    if (promptEl) promptEl.hidden = signedIn;
    if (!signedIn) {
      refreshSigninHref();
      // Clear any stale stamped values if the user signed out mid-session.
      if (urlInput) urlInput.value = '';
      if (refInput) refInput.value = '';
      if (previewEl) { previewEl.hidden = true; previewEl.textContent = ''; }
      setStatus('');
    }
  });
}
